import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import luxPlusGlossaryMessages from 'i18n/luxPlusGlossaryMessages'
import LuxPlusManageAccountModalBenefitsBlob from './LuxPlusManageAccountModalBenefitsBlob'
import LuxPlusManageAccountModalLoyaltyBenefitsBlob from './LuxPlusManageAccountModalLoyaltyBenefitsBlob'
import LuxPlusManageAccountModalMembershipStatusBanner from './LuxPlusManageAccountModalMembershipStatusBanner'
import LuxPlusManageAccountModalSavingsBanner from './LuxPlusManageAccountModalSavingsBanner'

interface Props {
  item: App.MemberSubscriptionItem
  onCancelRenewal: () => void
  onBack: () => void
}

function LuxPlusManageAccountModalRenewalCancellationConfirmation({
  item,
  onBack,
  onCancelRenewal,
}: Props) {
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])
  const intl = useIntl()

  const manageMembershipTitle = intl.formatMessage(luxPlusGlossaryMessages.manageMembership)

  const dontMissOfferHeading = intl.formatMessage({
    id: '9qexxp2FQoa7C6gSEyFV',
    defaultMessage: 'Don’t miss your exclusive renewal offer',
    description: 'Heading urging user not to miss renewal offer in cancellation confirmation modal',
  })

  const dontLoseBenefitsHeading = intl.formatMessage({
    id: 'f7Q3YMP5oeSbR2tRUveM',
    defaultMessage: 'Don’t lose your member only benefits',
    description: 'Heading urging user not to lose member benefits in cancellation confirmation modal',
  })

  const keepMembershipLabel = intl.formatMessage({
    id: 'NUrd0EoT_GA-e3WdJ5bo',
    defaultMessage: 'Keep my membership',
    description: 'Primary button text to keep membership in cancellation confirmation modal',
  })

  const cancelRenewalLabel = intl.formatMessage({
    id: 'ZRlgadQMqChkbclEZMMh',
    defaultMessage: 'Cancel renewal',
    description: 'Secondary button text to cancel renewal in cancellation confirmation modal',
  })

  return <>
    <ModalHeader title={manageMembershipTitle} onBackButtonClick={onBack} />
    <ModalBody>
      {isLoyaltyLuxPlusRenewalBonusEnabled && <ProductPaletteProvider palette={`tier-${tier}`}>
        <Sheet padding="24 16" background={`tier-${tier}-secondary`}>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading4">{dontMissOfferHeading}</Heading>
            <Heading variant="heading3" colour={tier === 'platinum' ? `tier-${tier}-dark-contrast` : `tier-${tier}-light-contrast`}>
              <FormattedMessage
                id="KjeZWdihr3uvrLYvaF9R"
                defaultMessage="<i>{statusCredits} and {points} is yours</i> with {loyaltyProgramName}."
                description="Heading showing loyalty status credits and points earned with loyalty program in cancellation confirmation modal"
                values={{
                  statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
                  points: <FormatLoyaltyPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
                  loyaltyProgramName: getLoyaltyProgramDisplayName(),
                  i: (chunks) => <i>{chunks}</i>,
                }}
              />
            </Heading>
            <Group direction="vertical" gap={16}>
              <LuxPlusManageAccountModalLoyaltyBenefitsBlob onLinkClick={dismissModal} />
              <BodyText variant="small">
                <FormattedMessage
                  id="89PaGVYFbgwxhDAid5sM"
                  defaultMessage="Offer only available on renewal of {programName} membership. <link>Terms & conditions apply</link>"
                  description="Disclaimer text with terms and conditions link for LuxPlus+ renewal bonus offer in cancellation confirmation modal"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    link: (chunks) => <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} onClick={dismissModal}>{chunks}</TextLink>,
                  }}
                />
              </BodyText>
            </Group>
          </Group>
        </Sheet>
      </ProductPaletteProvider>}
      {!isLoyaltyLuxPlusRenewalBonusEnabled && <Sheet background="lux-plus" padding="24 16">
        <Group direction="vertical" gap={24}>
          <Heading variant="heading4">{dontLoseBenefitsHeading}</Heading>
          <BodyText variant="medium">
            <FormattedMessage
              id="6i5WsgyguiMYNG9JR4SX"
              defaultMessage="<b>{programName}</b> pays for itself — and then some. With perks like early access, hidden deals, hotel bonuses and 24/7 support, it’s the easiest travel upgrade you’ll ever make."
              description="Persuasive body text describing LuxPlus+ value proposition in cancellation confirmation modal"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
        </Group>
      </Sheet>}
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={4}>
            {isLoyaltyLuxPlusRenewalBonusEnabled && <BodyText variant="medium" colour="lux-plus">
              <FormattedMessage
                id="NdxZhKBFf6wFaHnffCOx"
                defaultMessage="Plus unlimited <b>{programName}</b> member only benefits:"
                description="Subheading for member benefits list when loyalty renewal bonus is enabled in cancellation confirmation modal"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>}
            {!isLoyaltyLuxPlusRenewalBonusEnabled && <BodyText variant="medium" colour="lux-plus">
              <FormattedMessage
                id="HdKC-DiiOYj112_7kfTc"
                defaultMessage="Continue enjoying unlimited <b>{programName}</b> member only benefits:"
                description="Subheading for member benefits list when loyalty renewal bonus is not enabled in cancellation confirmation modal"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>}
            <LuxPlusManageAccountModalBenefitsBlob onLinkClick={dismissModal} />
          </Group>
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <LuxPlusManageAccountModalSavingsBanner />
            <LuxPlusManageAccountModalMembershipStatusBanner item={item} />
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{ children: keepMembershipLabel, onClick: dismissModal }}
      secondaryActionProps={{ children: cancelRenewalLabel, onClick: onCancelRenewal }}
    />
  </>
}

export default LuxPlusManageAccountModalRenewalCancellationConfirmation
